import { ActionConfig } from "@/types/products/product-config";
import { Ref } from "vue";
import { ProductFilter } from "@/composables/filters/product-filter";
import { CategoryFilter } from "@/composables/filters/category-filter";
import { PagingFilter } from "@/composables/filters/paging-filter";
import { useCoursesStore } from "@/stores/Courses";
import { type LabelService, useLabelService } from "@/composables/label-service";

import pinia from "@/plugins/PiniaPlugin";

import * as tmi from "@/constants";

import {
    articleKey, bonusReportKey, webinarKey,
    sectorSpotlightKey, sectorFocusSpotlightKey,
    contentWrapperKey, lessonsKey,
    majorIndexBreadthKey, modelPortfolioKey, fiveStarModelPortfolioKey,
    nyseBpiKey, portfolioBuilderKey, positionSizeCalculatorKey,
    relativeStrengthMatrixKey, scannerKey, sectorsKey, sectorAlertsKey,
    shadowSpikeAlertKey, spotlightVideoKey, tradeAlertKey, fiveStarTradeAlertKey,
    tutorialsKey, vimeoKey,
    categoryContextKey, contentSlugContextKey, courseSlugContextKey,
    placeholderContextKey, titleContextKey, vimeoIdContextKey,
} from "@/types/products/product-keys";

export * from "@/types/products/product-keys";

export type ProductDefinition = {
    actionsMap: ReadonlyMap< string, ActionConfig >,
    initialized: Ref< boolean >,
    initialView: string,
    logoPath: Ref< string | undefined >,
    darkLogoPath: Ref< string | undefined >,
    portfolioActionsMap?: ReadonlyMap< string, ActionConfig >,
    selectedView: Ref< string >
};

const labelService: LabelService = useLabelService();

const articleTutorialsDisplayText: string = 'Tutorials';
const bonusReportsDisplayText: string = 'Bonus Reports';
const introductionVideoDisplayText: string = 'Introduction';
const majorIndexBreadthDisplayText: string = 'Indexes';
const modelPortfolioDisplayText: string = 'Model Portfolio';
const fiveStarModelPortfolioDisplayText: string = '5-Star Portfolio';
const nyseBpiDisplayText: string = 'NYSE BPI';
// const stockReplacementStrategyCalculatorDisplayText: string = 'SRS Calculator';
const portfolioBuilderDisplayText: string = 'My Portfolio';
const positionSizeCalculatorDisplayText: string = 'Position Size Calc';
const productAlertsDisplayText: string = 'Alerts';
const fiveStarPortfolioAlertsDisplayText: string = '5-Star Alerts';
const productArticlesDisplayText: string = 'Articles';
const productLessonsDisplayText: string = 'Lessons';
const rsMatrixDisplayText: string = 'RS Matrix';
const sectorsDisplayText: string = 'Sectors';
const sectorAlertDisplayText: string = 'Sector Alerts';

const sectorSpotlightDisplayText = labelService.label( 'lblSectorSpotlight' );
const sectorSpotlightSearchPlaceholderText = `Search ${ sectorSpotlightDisplayText }`;

const sectorTreeDisplayText: string = 'Sector Tree';
const spotlightVideoDisplayText: string = 'Spotlight Videos';
const spreadCalculatorDisplayText: string = 'Spread Calculator';
const tradeAlertsScannerDisplayText: string = 'Scanner History';
const webinarDisplayText: string = 'Webinars';
const welcomeVideoDisplayText: string = 'Welcome Video';


export const articleTutorialsActionConfig: ActionConfig = {
    displayText: articleTutorialsDisplayText,
    componentKey: articleKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ tutorialsKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    },
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Tutorials' ],
        [ placeholderContextKey, 'Search Tutorials' ],
        [ categoryContextKey, tutorialsKey ]
    ] )
};

export const introductionVideoActionConfig: ActionConfig = {
    displayText: introductionVideoDisplayText,
    componentKey: vimeoKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Introduction to Options' ],
        [ vimeoIdContextKey, 788032335 ]
    ] )
};
export const modelPortfolioActionConfig: ActionConfig = { displayText: modelPortfolioDisplayText, componentKey: modelPortfolioKey };
export const fiveStarModelPortfolioActionConfig: ActionConfig = { displayText: fiveStarModelPortfolioDisplayText, componentKey: fiveStarModelPortfolioKey };

export const majorIndexBreadthActionConfig: ActionConfig = { displayText: majorIndexBreadthDisplayText, componentKey: majorIndexBreadthKey };
export const nyseBpiActionConfig: ActionConfig = { displayText: nyseBpiDisplayText, componentKey: nyseBpiKey };
// export const stockReplacementStrategyCalculatorActionConfig: ActionConfig = { displayText: stockReplacementStrategyCalculatorDisplayText, componentKey: stockReplacementStrategyCalculatorKey };
export const portfolioBuilderActionConfig: ActionConfig = { displayText: portfolioBuilderDisplayText, componentKey: portfolioBuilderKey };
export const positionSizeCalculatorActionConfig: ActionConfig = { displayText: positionSizeCalculatorDisplayText, componentKey: positionSizeCalculatorKey };
export const productAlertActionConfig: ActionConfig = {
    displayText: productAlertsDisplayText,
    componentKey: tradeAlertKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const fiveStarAlertActionConfig: ActionConfig = {
    displayText: fiveStarPortfolioAlertsDisplayText,
    componentKey: fiveStarTradeAlertKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const productArticlesActionConfig: ActionConfig = {
    displayText: productArticlesDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Articles & Commentary' ],
        [ placeholderContextKey, 'Search Articles' ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const productBonusReportsActionConfig: ActionConfig = {
    displayText: bonusReportsDisplayText,
    componentKey: bonusReportKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const cptProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'CheckPoint Trader Accelerated Program' ],
        [ courseSlugContextKey, 'checkpoint-trader-accelerated-program' ]
    ] ),
    reset: ( productSlug: string ) => {
        const courseStore = useCoursesStore( pinia );
        courseStore.setSelectedCourseByProductSlug( productSlug );
    }
};

export const dmiProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to Dark Money Indicator' ],
        [ courseSlugContextKey, 'dark-money-indicator-dmi' ]
    ] )
};

export const dmtProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to Deep Market Trader' ],
        [ courseSlugContextKey, 'welcome-to-deep-market-trader' ]
    ] )
};

export const osProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Options Soup' ],
        [ courseSlugContextKey, 'options-soup-oso' ]
    ] )
};

export const psProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Profit Skimmer: Intro Series' ],
        [ courseSlugContextKey, 'profit-skimmer-intro-series' ]
    ] )
};

export const sfProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Sector Focus: Intro to Sectors Series' ],
        [ courseSlugContextKey, 'sector-focus-intro-to-sectors-series' ]
    ] )
};

export const spProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to Sector Prophets' ],
        [ courseSlugContextKey, 'welcome-to-sector-prophets' ]
    ] )
};

export const tamProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ courseSlugContextKey, 'technical-analysis-millionaire-tam' ],
        [ titleContextKey, 'Technical Analysis Millionaire (TAM)' ]
    ] )
};

export const tspProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Crypto Quantum Leap Course' ],
        [ courseSlugContextKey, 'crypto-quantum-leap-course' ]
    ] )
};

export const relativeStrengthMatrixActionConfig: ActionConfig = { displayText: rsMatrixDisplayText, componentKey: relativeStrengthMatrixKey };
export const sectorsActionConfig: ActionConfig = { displayText: sectorsDisplayText, componentKey: sectorsKey };
export const sectorAlertsActionConfig: ActionConfig = { displayText: sectorAlertDisplayText, componentKey: sectorAlertsKey };

export const sectorSpotlightActionConfig: ActionConfig = {
    displayText: sectorSpotlightDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, sectorSpotlightDisplayText ],
        [ placeholderContextKey, sectorSpotlightSearchPlaceholderText ],
        [ categoryContextKey, sectorSpotlightKey ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ sectorSpotlightKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const sectorFocusSpotlightActionConfig: ActionConfig = {
    displayText: sectorSpotlightDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, sectorSpotlightDisplayText ],
        [ placeholderContextKey, sectorSpotlightSearchPlaceholderText ],
        [ categoryContextKey, sectorFocusSpotlightKey ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ sectorFocusSpotlightKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const sectorTreeActionConfig: ActionConfig = {
    displayText: sectorTreeDisplayText,
    componentKey: contentWrapperKey,
    ctx: new Map< string, any >( [
        [ contentSlugContextKey, 'sector-tree' ]
    ] )
};

export const spotlightVideoActionConfig: ActionConfig = {
    displayText: spotlightVideoDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Spotlight Videos' ],
        [ placeholderContextKey, 'Search Spotlight Videos' ],
        [ categoryContextKey, spotlightVideoKey ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ spotlightVideoKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const spreadCalculatorActionConfig: ActionConfig = { displayText: spreadCalculatorDisplayText, componentKey: tmi.SpreadCalculatorKey };
export const tamIntroductionVideoActionConfig: ActionConfig = {
    displayText: introductionVideoDisplayText,
    componentKey: vimeoKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to the Technical Analysis Millionaire Course' ],
        [ vimeoIdContextKey, 838414602 ]
    ] )
};
export const tradeAlertsScannerActionConfig: ActionConfig = {
    displayText: tradeAlertsScannerDisplayText,
    componentKey: scannerKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Shadow Spike Alerts' ],
        [ placeholderContextKey, 'Search Shadow Spike Alerts' ],
        [ categoryContextKey, shadowSpikeAlertKey ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ shadowSpikeAlertKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const tspModelPortfolioActionConfig: ActionConfig = {
    displayText: modelPortfolioDisplayText,
    componentKey: contentWrapperKey,
    ctx: new Map< string, any >( [
        [ contentSlugContextKey, 'tsp-portfolio-table' ]
    ] )
};

export const webinarActionConfig: ActionConfig = {
    displayText: webinarDisplayText,
    componentKey: webinarKey,
    ctx: new Map< string, any >( [
        [ placeholderContextKey, 'Search Webinars' ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const welcomeVideoActionConfig: ActionConfig = {
    displayText: welcomeVideoDisplayText,
    componentKey: vimeoKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Monthly Trend Trader - Welcome Video' ],
        [ vimeoIdContextKey, 529362272 ]
    ] )
};

